import React, { useState } from 'react';
import {
    Container,
    Paper,
    Title,
    Text,
    Stack,
    Grid,
    Tabs,
    Loader,
    Alert,
    Button,
    Group,
    Badge,
    Divider,
} from '@mantine/core';
import { IconAlertCircle, IconRefresh, IconArrowLeft } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useMatchAnalytics from 'hooks/useMatchAnalytics';
import { PlayerStatsCard } from './PlayerStatsCard';
import { MVPBadge } from './MVPBadge';
import { HighlightsList } from './HighlightsList';
import { TeamComparison } from './TeamComparison';
import analyticsService from 'services/analyticsService';
import { formatDateTime } from '../TournamentsPage';
import Subtitle from '../Subtitle';

export const MatchAnalyticsPage: React.FC = () => {
    const { matchId } = useParams<{ matchId: string }>();
    const navigate = useNavigate();
    const { data, loading, error, refresh } = useMatchAnalytics(matchId || '');
    const [teamAnalytics, setTeamAnalytics] = useState<any>(null);
    const [teamAnalyticsLoading, setTeamAnalyticsLoading] = useState(false);

    React.useEffect(() => {
        if (data?.matchId) {
            setTeamAnalyticsLoading(true);
            analyticsService
                .fetchTeamAnalytics(data.matchId)
                .then((result) => setTeamAnalytics(result.data))
                .catch(() => {})
                .finally(() => setTeamAnalyticsLoading(false));
        }
    }, [data?.matchId]);

    if (loading) {
        return (
            <Container size="lg">
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                    <Group justify="center" p="xl">
                        <Loader size="lg" />
                        <Text>Ladataan otteluanalytiikkaa...</Text>
                    </Group>
                </Paper>
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container size="lg">
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                    <Alert
                        color="red"
                        icon={<IconAlertCircle size={16} />}
                        title="Analytiikkaa ei saatu ladattua"
                        withCloseButton
                        onClose={refresh}
                    >
                        {error || 'Ottelua ei löytynyt'}
                    </Alert>
                    <Button mt="md" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/tournaments')}>
                        Takaisin turnauksiin
                    </Button>
                </Paper>
            </Container>
        );
    }

    const { map, teams, playedAt, durationMinutes, totalRounds, finalScore, mvp, playerStats, highlights } = data;

    return (
        <Container size="lg">
            <Stack gap="md">
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                    <Group justify="space-between" mb="md">
                        <Button
                            variant="subtle"
                            leftSection={<IconArrowLeft size={16} />}
                            onClick={() => navigate('/tournaments')}
                        >
                            Takaisin
                        </Button>
                        <Button variant="subtle" leftSection={<IconRefresh size={16} />} onClick={refresh}>
                            Päivitä
                        </Button>
                    </Group>

                    <Stack gap="sm">
                        <Title order={2}>Otteluanalytiikka</Title>
                        <Group gap="md">
                            <Text size="sm" c="dimmed">
                                {formatDateTime(playedAt)}
                            </Text>
                            <Badge>{map}</Badge>
                            <Badge variant="light">BO{teams[0]?.score + teams[1]?.score || totalRounds}</Badge>
                            <Badge variant="light">{durationMinutes} min</Badge>
                        </Group>

                        <Group gap="lg" mt="md">
                            <div>
                                <Text size="lg" fw={600}>
                                    {teams[0]?.name || 'Team 1'}
                                </Text>
                                <Text size="xl" fw={700} c={finalScore.CT > finalScore.T ? 'blue' : 'dimmed'}>
                                    {finalScore.CT}
                                </Text>
                            </div>
                            <Text size="xl" fw={700} c="dimmed">
                                vs
                            </Text>
                            <div>
                                <Text size="lg" fw={600}>
                                    {teams[1]?.name || 'Team 2'}
                                </Text>
                                <Text size="xl" fw={700} c={finalScore.T > finalScore.CT ? 'orange' : 'dimmed'}>
                                    {finalScore.T}
                                </Text>
                            </div>
                        </Group>

                        <Divider my="md" />

                        <Group gap="md">
                            <Text size="sm" c="dimmed">Match MVP:</Text>
                            <MVPBadge playerName={mvp.match.playerName} mvpScore={mvp.match.mvpScore} variant="match" />
                        </Group>
                    </Stack>
                </Paper>

                <Tabs defaultValue="overview">
                    <Tabs.List>
                        <Tabs.Tab value="overview">Yleiskuva</Tabs.Tab>
                        <Tabs.Tab value="players">Pelaajat</Tabs.Tab>
                        <Tabs.Tab value="highlights">Korostukset</Tabs.Tab>
                        <Tabs.Tab value="teams">Joukkueet</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="overview" pt="md">
                        <Stack gap="md">
                            <Paper shadow="sm" p="md" radius="md" withBorder>
                                <Subtitle size="normal" title="Match MVP" />
                                <Grid gutter="md" mt="md">
                                    {mvp.match && (
                                        <Grid.Col span={{ base: 12, md: 6 }}>
                                            <PlayerStatsCard player={mvp.match} showDetailed />
                                        </Grid.Col>
                                    )}
                                    {(mvp.teams.CT || mvp.teams.T) && (
                                        <Grid.Col span={{ base: 12, md: 6 }}>
                                            <Stack gap="md">
                                                {mvp.teams.CT && (
                                                    <div>
                                                        <Text size="sm" c="dimmed" mb="xs">CT MVP</Text>
                                                        <PlayerStatsCard player={mvp.teams.CT} />
                                                    </div>
                                                )}
                                                {mvp.teams.T && (
                                                    <div>
                                                        <Text size="sm" c="dimmed" mb="xs">T MVP</Text>
                                                        <PlayerStatsCard player={mvp.teams.T} />
                                                    </div>
                                                )}
                                            </Stack>
                                        </Grid.Col>
                                    )}
                                </Grid>
                            </Paper>

                            {highlights && highlights.length > 0 && (
                                <Paper shadow="sm" p="md" radius="md" withBorder>
                                    <Subtitle size="normal" title="Top Highlights" />
                                    <HighlightsList highlights={highlights} limit={5} />
                                </Paper>
                            )}

                            {data.progression?.mostImproved && (
                                <Paper shadow="sm" p="md" radius="md" withBorder>
                                    <Subtitle size="normal" title="Parannus" />
                                    <Text size="sm">
                                        <strong>{data.progression.mostImproved.playerName}</strong> parani eniten ottelun
                                        aikana (improvement score: {data.progression.mostImproved.improvementScore.toFixed(1)})
                                    </Text>
                                </Paper>
                            )}
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="players" pt="md">
                        <Paper shadow="sm" p="md" radius="md" withBorder>
                            <Subtitle size="normal" title="Pelaajatilastot" />
                            <Grid gutter="md" mt="md">
                                {playerStats.map((player, index) => (
                                    <Grid.Col key={player?.steamId || `player-${index}`} span={{ base: 12, md: 6 }}>
                                        <PlayerStatsCard player={player} showDetailed />
                                    </Grid.Col>
                                ))}
                            </Grid>
                        </Paper>
                    </Tabs.Panel>

                    <Tabs.Panel value="highlights" pt="md">
                        <Paper shadow="sm" p="md" radius="md" withBorder>
                            <Subtitle size="normal" title="Korostukset" />
                            <HighlightsList highlights={highlights} />
                        </Paper>
                    </Tabs.Panel>

                    <Tabs.Panel value="teams" pt="md">
                        {teamAnalyticsLoading ? (
                            <Paper shadow="sm" p="md" radius="md" withBorder>
                                <Group justify="center" p="xl">
                                    <Loader />
                                    <Text>Ladataan joukkuetilastoja...</Text>
                                </Group>
                            </Paper>
                        ) : teamAnalytics ? (
                            <TeamComparison data={teamAnalytics} />
                        ) : (
                            <Paper shadow="sm" p="md" radius="md" withBorder>
                                <Alert color="yellow" title="Joukkuetilastot eivät ole saatavilla" />
                            </Paper>
                        )}
                    </Tabs.Panel>
                </Tabs>
            </Stack>
        </Container>
    );
};

export default MatchAnalyticsPage;

